import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于51漫画 - 品牌介绍',
    description: '51漫画品牌介绍、口碑评价与联系方式。提供51漫画免费下载、免费版下载与APP下载服务。',
    url: 'https://51manhua.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '51漫画',
      url: 'https://51manhua.com',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '51漫画',
    url: 'https://51manhua.com',
    description: '51漫画提供海量漫画全集免费离线阅读，支持手机客户端安装，随时随地畅享漫画阅读。',
    foundingDate: '2018',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'service@51manhua.com',
      availableLanguage: 'Chinese',
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }
    try {
      const formData = new FormData(form);
      if (honeypotInput) {
        formData.delete('website_alt');
      }
      const response = await fetch('https://readdy.ai/api/form/d9omq5iftmtnfcfh09ug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请重试。';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试。');
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Brand Introduction */}
          <section className="mb-14">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-6">
              关于<strong>51漫画</strong>
            </h1>
            <div className="prose max-w-none">
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                <strong>51漫画</strong>成立于2018年，是一家专注于漫画资源整合与分发的互联网平台。
                我们致力于为广大漫画爱好者提供<strong>51漫画免费下载</strong>、<strong>免费版下载</strong>与<strong>APP下载</strong>服务，
                让每一位用户都能轻松获取海量漫画资源，实现<strong>漫画全集免费离线阅读</strong>。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                经过多年发展，51漫画已积累了超过15部精品漫画的完整资源库，涵盖热血格斗、恋爱校园、玄幻奇幻、
                悬疑推理、搞笑日常、科幻未来等六大热门分类。我们的漫画资源总数超过1800话，累计阅读量突破2.3亿次，
                用户好评率高达99.7%。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                51漫画始终坚持"免费、便捷、优质"的服务理念。用户可以通过<strong>51漫画APP</strong>一键下载喜爱的漫画，
                支持<strong>离线阅读</strong>功能，无需网络连接也能随时随地畅享漫画世界。
                我们的<strong>手机客户端</strong>同时支持Android和iOS两大主流平台，安装简便，运行流畅。
              </p>
            </div>
          </section>

          {/* Features Grid */}
          <section className="mb-14">
            <h2 className="text-xl font-heading text-foreground-950 mb-6">
              我们的<strong>优势</strong>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: 'ri-download-cloud-2-line', title: '海量资源', desc: '15+精品漫画，1800+漫画话数，持续更新中' },
                { icon: 'ri-smartphone-line', title: '多平台支持', desc: 'Android、iOS、Windows全平台客户端覆盖' },
                { icon: 'ri-wifi-off-line', title: '离线阅读', desc: '下载后无需网络，随时随地畅享漫画世界' },
                { icon: 'ri-shield-check-line', title: '安全可靠', desc: '严格的内容审核，保障用户阅读体验' },
              ].map((feat, i) => (
                <div key={i} className="bg-background-100 rounded-lg p-5 text-center border border-background-200/70">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className={`${feat.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1.5">{feat.title}</h3>
                  <p className="text-xs text-foreground-500">{feat.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation / Testimonials */}
          <section className="mb-14">
            <h2 className="text-xl font-heading text-foreground-950 mb-6">
              用户<strong>口碑</strong>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  name: '漫画爱好者小王',
                  rating: 5,
                  text: '51漫画真的太方便了！资源丰富，下载速度快，离线阅读功能非常好用。上班通勤路上看漫画再也不用担心流量了。',
                },
                {
                  name: '大学生小李',
                  rating: 5,
                  text: '从大一开始用51漫画，到现在快三年了。界面简洁，分类清晰，更新速度也很快。强烈推荐给所有喜欢看漫画的朋友！',
                },
                {
                  name: '资深漫迷老张',
                  rating: 5,
                  text: '用了很多漫画APP，51漫画是最稳定的一个。资源齐全，画质清晰，关键是免费的！这么多年一直在用，真心不错。',
                },
              ].map((review, i) => (
                <div key={i} className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: review.rating }).map((_, j) => (
                      <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">{review.text}</p>
                  <p className="text-xs text-foreground-400 font-medium">—— {review.name}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section className="mb-14">
            <h2 className="text-xl font-heading text-foreground-950 mb-6">
              <strong>联系我们</strong>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-base font-semibold text-foreground-900 mb-4">联系方式</h3>
                <div className="space-y-3">
                  {[
                    { icon: 'ri-mail-line', label: '电子邮箱', value: 'service@51manhua.com' },
                    { icon: 'ri-wechat-line', label: '微信公众号', value: '51漫画官方' },
                    { icon: 'ri-qq-line', label: 'QQ交流群', value: '88205151' },
                    { icon: 'ri-weibo-line', label: '官方微博', value: '@51漫画官方微博' },
                  ].map((contact, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                        <i className={contact.icon}></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">{contact.label}</p>
                        <p className="text-sm text-foreground-800 font-medium">{contact.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-base font-semibold text-foreground-900 mb-4">在线留言</h3>
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的昵称"
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                      required
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      rows={4}
                      placeholder="请输入您的留言..."
                      maxLength={500}
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                      required
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                  </div>
                  <input
                    type="text"
                    name="website_alt"
                    className="website-alt-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <button
                    type="submit"
                    className="w-full px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    提交留言
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 mt-2">留言提交成功！我们会尽快回复您。</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 mt-2">{formError || '提交失败，请重试。'}</p>
                  )}
                </form>
              </div>
            </div>
          </section>

          {/* FAQ Schema for About page */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: [
                  {
                    '@type': 'Question',
                    name: '51漫画是什么？',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: '51漫画是一家专注于漫画资源整合与分发的互联网平台，提供51漫画免费下载、免费版下载与APP下载服务。',
                    },
                  },
                  {
                    '@type': 'Question',
                    name: '51漫画如何联系客服？',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: '您可以通过电子邮箱service@51manhua.com、微信公众号"51漫画官方"或QQ交流群88205151联系我们。',
                    },
                  },
                ],
              }),
            }}
          />
        </div>
      </main>

      <Footer />
    </div>
  );
}